import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent,
  type PointerEvent,
  type RefObject,
} from "react";
import {
  footprint,
  getItemDef,
  type Inventory,
  type ItemInstance,
  type PlacedItem,
} from "@rietz/shared";
import { uiActions } from "../actions.js";
import { openDialog, setModWeapon, type OpenContainer } from "../store.js";
import {
  centreGrab,
  actionAt,
  computeHover,
  dropOp,
  instanceAt,
  toggleRotation,
  type DragState,
  type ItemSource,
} from "./drag.js";
import type { MenuState } from "./menu.js";
import { quickMove } from "./quickmove.js";
import { searchDropLock } from "./search.js";
import { TOOLTIP_DELAY_MS, type TooltipState } from "./tooltip.js";

/**
 * The pointer half of an inventory screen, as a hook: picking up, carrying,
 * rotating, dropping, right-clicking and dwelling — with NO opinion about what
 * panels are on screen, so the raid's screen and the menu's stash share one
 * gesture. Only TRANSIENT state (the drag, the menu, the dwell); the inventory
 * lives in the store. POINTER CAPTURE goes to `root.ref`'s element, which must
 * cover the screen and outlive everything inside it — a tile can vanish
 * mid-drag.
 */

/** How far a press must travel before it is a drag rather than a click. */
const DRAG_THRESHOLD_PX = 4;

interface PendingDrag {
  pointerId: number;
  startX: number;
  startY: number;
  /** The drag as it would start, minus the hover that `resolve` adds. */
  drag: DragState;
}

/** Spread onto every `GridPanel` and `EquipmentDoll`. */
export interface TileHandlers {
  onTilePointerDown: (
    ev: PointerEvent<HTMLDivElement>,
    placed: PlacedItem,
    from: ItemSource,
  ) => void;
  onTileContextMenu: (
    ev: MouseEvent<HTMLDivElement>,
    placed: PlacedItem,
    from: ItemSource,
  ) => void;
  onTileDoubleClick: (
    ev: MouseEvent<HTMLDivElement>,
    placed: PlacedItem,
    from: ItemSource,
  ) => void;
  onTileHover: (
    ev: PointerEvent<HTMLDivElement>,
    placed: PlacedItem,
    from: ItemSource,
  ) => void;
  onTileLeave: () => void;
}

/** Spread onto the backdrop element that owns capture — see the note above. */
export interface DragRootProps {
  ref: RefObject<HTMLDivElement | null>;
  onPointerMove: (ev: PointerEvent<HTMLDivElement>) => void;
  onPointerUp: (ev: PointerEvent<HTMLDivElement>) => void;
  onPointerCancel: (ev: PointerEvent<HTMLDivElement>) => void;
  onPointerDown: () => void;
  onContextMenu: (ev: MouseEvent<HTMLDivElement>) => void;
}

export interface InventoryDragApi {
  drag: DragState | null;
  menu: MenuState | null;
  /** What the dwell timer decided to describe, or `null`. */
  tip: TooltipState | null;
  tiles: TileHandlers;
  root: DragRootProps;
  closeMenu: () => void;
  /** Abandons a drag, a menu and a hover in one go — for a screen closing. */
  clear: () => void;
}

/**
 * One screen-specific destination on top of ordinary placement — the trader's
 * counter. Screens that pass no options keep their behaviour byte-for-byte.
 */
export interface InventoryDragOptions {
  /** The menu stash may contain the weapon being edited; raid containers may not. */
  containerOwned?: boolean;
  action?: {
    id: string;
    accepts: (from: ItemSource, item: ItemInstance) => boolean;
    drop: (from: ItemSource, item: ItemInstance) => void;
  };
  /** Overrides the normal Ctrl+click transfer for screens where the shortcut
   *  has a stronger local meaning (selling while a trader is open). */
  onQuickMove?: (from: ItemSource, item: ItemInstance) => void;
}

export function useInventoryDrag(
  inv: Inventory | null,
  container: OpenContainer | null,
  options: InventoryDragOptions = {},
): InventoryDragApi {
  const [drag, setDrag] = useState<DragState | null>(null);
  const [menu, setMenu] = useState<MenuState | null>(null);

  /**
   * The tooltip in two halves: `hover` is WHICH tile the pointer is on (so a
   * twitching mouse does not restart the dwell timer; the position rides in a
   * ref), `tip` is what the timer decided to show.
   */
  const [hover, setHover] = useState<{ iid: string; source: ItemSource } | null>(null);
  const hoverPos = useRef({ x: 0, y: 0 });
  const [tip, setTip] = useState<TooltipState | null>(null);

  const rootRef = useRef<HTMLDivElement | null>(null);

  /**
   * A press that has not yet become a drag. Armed only after
   * `DRAG_THRESHOLD_PX` of travel, so a click never takes pointer capture and
   * the `dblclick` lands on the tile — what lets double-clicking a bag open it.
   */
  const pending = useRef<PendingDrag | null>(null);

  // The keyboard listener wants the CURRENT drag without being torn down and
  // rebuilt on every pointer move, which is what a `drag` dependency would do.
  const dragRef = useRef<DragState | null>(null);
  dragRef.current = drag;

  /** Re-resolves a drag against the layout and the rules as they are now. */
  const resolve = useCallback(
    (d: DragState): DragState => {
      const def = getItemDef(d.defId);
      if (def === null || inv === null) return { ...d, hover: null };
      const action = options.action;
      if (action !== undefined && actionAt(d.px, d.py) === action.id) {
        const item = instanceAt(inv, container, d.from, d.iid);
        return {
          ...d,
          hover: {
            t: "action",
            action: action.id,
            ok: item !== null && action.accepts(d.from, item),
          },
        };
      }
      // A PREDICATE that reads the store when asked, not a captured value: the
      // search sweep moves on a timer. The menu's screens have no search
      // targets.
      return {
        ...d,
        hover: computeHover(
          d,
          def,
          inv,
          container,
          options.containerOwned === true,
          searchDropLock(container),
        ),
      };
    },
    [inv, container, options.action, options.containerOwned],
  );

  const dragging = drag !== null;
  const menuOpen = menu !== null;

  useEffect(() => {
    if (!dragging && !menuOpen) return;
    const onKey = (ev: KeyboardEvent): void => {
      if (ev.code === "Escape") {
        // The GAME also listens for Escape ("close the screen"); a drag or menu
        // is
        // the more local thing to cancel, so it is cancelled here and
        // swallowed.
        ev.preventDefault();
        ev.stopPropagation();
        setDrag(null);
        setMenu(null);
        return;
      }
      if (ev.code === "KeyR" && dragRef.current !== null) {
        ev.preventDefault();
        setDrag((d) => (d === null ? null : resolve(toggleRotation(d))));
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
    };
  }, [dragging, menuOpen, resolve]);

  // The dwell timer. Dragging or an open menu suppresses the tooltip — a panel
  // over the item you carry is in the way.
  useEffect(() => {
    if (hover === null || dragging || menuOpen) {
      setTip(null);
      return;
    }
    const timer = window.setTimeout(() => {
      const { x, y } = hoverPos.current;
      setTip({ iid: hover.iid, source: hover.source, px: x, py: y });
    }, TOOLTIP_DELAY_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [hover, dragging, menuOpen]);

  const onTilePointerDown = (
    ev: PointerEvent<HTMLDivElement>,
    placed: PlacedItem,
    from: ItemSource,
  ): void => {
    if (ev.button !== 0) return;
    const def = getItemDef(placed.item.defId);
    if (def === null) return;
    ev.preventDefault();
    // Kept off the root's own handler, which reads a press on the dim as
    // "dismiss the menu" rather than "pick this up".
    ev.stopPropagation();
    setMenu(null);
    setHover(null);

    // Ctrl+click is a TRANSFER, not a drag: no capture, no drag state.
    if (ev.ctrlKey) {
      if (from.t === "mod") return;
      if (options.onQuickMove === undefined) quickMove(from, placed.item.iid);
      else options.onQuickMove(from, placed.item);
      return;
    }

    // The item is carried by its centre, wherever on the tile it was pressed —
    // see `centreGrab`.
    const grab = centreGrab(footprint(def, placed.rot));
    pending.current = {
      pointerId: ev.pointerId,
      startX: ev.clientX,
      startY: ev.clientY,
      drag: {
        pointerId: ev.pointerId,
        iid: placed.item.iid,
        defId: placed.item.defId,
        from,
        origin: { x: placed.x, y: placed.y, rot: placed.rot },
        rot: placed.rot,
        px: ev.clientX,
        py: ev.clientY,
        grabX: grab.x,
        grabY: grab.y,
        hover: null,
      },
    };
  };

  const onDragMove = (ev: PointerEvent<HTMLDivElement>): void => {
    const p = pending.current;
    if (p !== null && p.pointerId === ev.pointerId) {
      if (Math.hypot(ev.clientX - p.startX, ev.clientY - p.startY) < DRAG_THRESHOLD_PX) return;
      pending.current = null;
      // Capture is taken only now, when the press has become a drag — see
      // `pending` above for why it is not taken on the press itself.
      rootRef.current?.setPointerCapture(ev.pointerId);
      setDrag(resolve({ ...p.drag, px: ev.clientX, py: ev.clientY }));
      return;
    }
    setDrag((d) =>
      d === null || d.pointerId !== ev.pointerId
        ? d
        : resolve({ ...d, px: ev.clientX, py: ev.clientY }),
    );
  };

  const onDragEnd = (ev: PointerEvent<HTMLDivElement>): void => {
    if (pending.current?.pointerId === ev.pointerId) pending.current = null;
    const d = dragRef.current;
    if (d === null || d.pointerId !== ev.pointerId) return;
    setDrag(null);
    // Resolved once more at the release point: a pointerup can carry a position
    // no pointermove ever reported, and that last pixel decides the cell.
    const resolved = resolve({ ...d, px: ev.clientX, py: ev.clientY });
    const action = options.action;
    if (
      resolved.hover?.t === "action" &&
      resolved.hover.ok &&
      action !== undefined &&
      resolved.hover.action === action.id &&
      inv !== null
    ) {
      const item = instanceAt(inv, container, resolved.from, resolved.iid);
      if (item !== null) action.drop(resolved.from, item);
      return;
    }
    const op = dropOp(resolved);
    if (op !== null) uiActions().tryOp(op);
  };

  const onDragCancel = (ev: PointerEvent<HTMLDivElement>): void => {
    if (pending.current?.pointerId === ev.pointerId) pending.current = null;
    setDrag((d) => (d === null || d.pointerId !== ev.pointerId ? d : null));
  };

  const onTileContextMenu = (
    ev: MouseEvent<HTMLDivElement>,
    placed: PlacedItem,
    from: ItemSource,
  ): void => {
    ev.preventDefault();
    ev.stopPropagation();
    if (dragRef.current !== null) return;
    setHover(null);
    setMenu({ px: ev.clientX, py: ev.clientY, iid: placed.item.iid, source: from });
  };

  const onTileDoubleClick = (
    ev: MouseEvent<HTMLDivElement>,
    placed: PlacedItem,
    from: ItemSource,
  ): void => {
    const def = getItemDef(placed.item.defId);
    if (def === null) return;
    // A weapon opens the workbench instead, as the menu's "Inspect" routes a
    // gun there.
    const modifiable =
      def.kind === "weapon" &&
      (from.t === "slot" || from.t === "bodySlot"
        ? from.slot === "pistol" || from.slot === "weapon" || from.slot === "weapon2"
        : from.t === "grid" || from.t === "box") &&
      (def.slots?.length ?? 0) > 0;
    // A body's bags never open on double-click — they are already drawn open
    // inline. His RIFLE does go on the bench: how a scope is looted without the
    // gun.
    if (from.t === "bodySlot" && !modifiable) return;
    if (def.kind !== "container" && !modifiable) return;
    ev.preventDefault();
    ev.stopPropagation();
    // Opening wins over whatever the two presses before this left behind.
    pending.current = null;
    setDrag(null);
    setMenu(null);
    setHover(null);
    if (modifiable) setModWeapon(placed.item.iid);
    else openDialog("box", placed.item.iid);
  };

  const onTileHover = (
    ev: PointerEvent<HTMLDivElement>,
    placed: PlacedItem,
    from: ItemSource,
  ): void => {
    // A drag owns the pointer; the tile it happens to be flying over is not
    // something the player is asking about.
    if (dragRef.current !== null) return;
    hoverPos.current = { x: ev.clientX, y: ev.clientY };
    // Same object back for the same tile: React bails out of the re-render and
    // the effect above sees no change, so the dwell timer keeps running.
    setHover((h) =>
      h !== null && h.iid === placed.item.iid ? h : { iid: placed.item.iid, source: from },
    );
  };

  const onTileLeave = (): void => {
    setHover(null);
  };

  const clear = useCallback((): void => {
    pending.current = null;
    setDrag(null);
    setMenu(null);
    setHover(null);
  }, []);

  const closeMenu = useCallback((): void => {
    setMenu(null);
  }, []);

  return {
    drag,
    menu,
    tip,
    tiles: {
      onTilePointerDown,
      onTileContextMenu,
      onTileDoubleClick,
      onTileHover,
      onTileLeave,
    },
    root: {
      ref: rootRef,
      onPointerMove: onDragMove,
      onPointerUp: onDragEnd,
      onPointerCancel: onDragCancel,
      // A release out here — on the dim, on nothing — is a cancel, and it has
      // to be caught because pointer capture ends the moment the button does.
      onPointerDown: () => {
        setMenu(null);
      },
      // Right-clicking anywhere on an inventory screen is an inventory gesture,
      // never a request for the browser's menu.
      onContextMenu: (ev: MouseEvent<HTMLDivElement>) => {
        ev.preventDefault();
      },
    },
    closeMenu,
    clear,
  };
}
